import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import PortalTable, { PortalFieldDef } from '../util/PortalTable';

const autoSlug = (v: string) => v.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

const MODAL_STYLE = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 380, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 3,
};

function PtModal({ open, onClose, onSaved, editing }: { open: boolean; onClose: () => void; onSaved: () => void; editing: any | null }) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setName(editing?.name ?? '');
      setSlug(editing?.slug ?? '');
      setSortOrder(String(editing?.sortOrder ?? 0));
      setIsActive(editing?.isActive ?? true);
    }
  }, [open, editing]);

  const handleSave = async () => {
    if (!name.trim()) { enqueueSnackbar('Name is required', { variant: 'error' }); return; }
    const payload = { name: name.trim(), slug: slug || autoSlug(name), sortOrder: Number(sortOrder), isActive };
    try {
      if (editing) {
        await axios.put(`crm/project-types/${editing.id}`, payload);
      } else {
        await axios.post('crm/project-types', payload);
      }
      enqueueSnackbar(editing ? 'Updated' : 'Created', { variant: 'success' });
      onSaved();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message ?? 'Error', { variant: 'error' });
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={MODAL_STYLE}>
          <div className="text-[16px] font-semibold mb-[14px] text-[var(--text-color)]">
            {editing ? t('Edit') : t('Create')} {t('ProjectType')}
          </div>
          <div className="flex flex-col gap-[10px]">
            <div>
              <label className="text-[12px] ml-[2px] text-[var(--text-color)]">{t('Name')}</label>
              <input title={t('Name')} placeholder={t('Name')}
                className="w-full text-[14px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input mt-[2px]"
                value={name}
                onChange={(e) => { setName(e.target.value); if (!editing) setSlug(autoSlug(e.target.value)); }} />
            </div>
            <div>
              <label className="text-[12px] ml-[2px] text-[var(--text-color)]">{t('Slug')}</label>
              <input title={t('Slug')} placeholder={t('Slug')}
                className="w-full text-[14px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input mt-[2px]"
                value={slug} onChange={(e) => setSlug(autoSlug(e.target.value))} />
            </div>
            <div>
              <label className="text-[12px] ml-[2px] text-[var(--text-color)]">{t('SortOrder')}</label>
              <input title={t('SortOrder')} placeholder="0" type="number"
                className="w-full text-[14px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input mt-[2px]"
                value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            </div>
            <label className="flex items-center gap-[8px] text-[13px] cursor-pointer text-[var(--text-color)]">
              <input type="checkbox" title={t('IsActive')} checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              {t('IsActive')}
            </label>
          </div>
          <div className="flex gap-[10px] mt-[16px]">
            <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={handleSave}>
              {t('Change')}
            </Button>
            <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} onClick={onClose}>
              {t('Cancel')}
            </Button>
          </div>
        </Box>
      </Fade>
    </Modal>
  );
}

export default function CrmProjectTypes() {
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<any[]>([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const load = async () => {
    try {
      const { data } = await axios.get('crm/project-types');
      setItems(Array.isArray(data) ? data : []);
    } catch { enqueueSnackbar('Error loading', { variant: 'error' }); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('AreYouSure') + '?')) return;
    try {
      await axios.delete(`crm/project-types/${id}`);
      enqueueSnackbar('Deleted', { variant: 'success' });
      load();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message ?? 'Error', { variant: 'error' });
    }
  };

  const handleDeleteMany = async (ids: (string | number)[]) => {
    await Promise.all(ids.map((id) => axios.delete(`crm/project-types/${id}`)));
    enqueueSnackbar('Deleted', { variant: 'success' });
    load();
  };

  const handleInlineAdd = async (data: Record<string, any>) => {
    if (!data.name?.trim()) {
      enqueueSnackbar('Name is required', { variant: 'error' });
      throw new Error('validation');
    }
    await axios.post('crm/project-types', {
      name: data.name.trim(),
      slug: data.slug || autoSlug(data.name),
      sortOrder: 0,
      isActive: data.isActive ?? true,
    });
    enqueueSnackbar('Created', { variant: 'success' });
    load();
  };

  const fields: PortalFieldDef[] = [
    { key: 'name',     label: t('Name'),     type: 'text',    showInTable: true,  editable: true, defaultValue: '', slugFrom: undefined },
    { key: 'slug',     label: t('Slug'),     type: 'slug',    showInTable: false, editable: true, defaultValue: '', slugFrom: 'name' },
    { key: 'isActive', label: t('IsActive'), type: 'boolean', showInTable: true, editable: true, defaultValue: true },
  ];

  return (
    <>
      <PortalTable
        title={t('ProjectType')}
        rows={items}
        getKey={(pt) => pt.id}
        fields={fields}
        onInlineAdd={handleInlineAdd}
        addLabel={`${t('Create')} ${t('ProjectType')}`}
        onEdit={(pt) => { setEditing(pt); setModal(true); }}
        onDelete={(pt) => handleDelete(pt.id)}
        onDeleteMany={handleDeleteMany}
        getRowOpacity={(pt) => pt.isActive ? 1 : 0.45}
      />
      <PtModal open={modal} onClose={() => setModal(false)} onSaved={load} editing={editing} />
    </>
  );
}
